import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Select from "@/components/shared/select";
import {
  ExploreCustomField,
  ExploreEnrichment,
  ExploreInteractionType,
  exploreInteractionTypeToLabel,
} from "@/v2/entities/explore/Explore.model";
import {
  ExploreGroupByColumnType,
  exploreGroupByColumnTypeToLabel,
  exploreGroupByColumnTypes,
} from "@/v2/entities/explore/columns/ExploreColum.model";
import { Tabs, TabsList, TabsTrigger } from "@/v2/components/dataTabs/tabs";
import { IceCubesIcon } from "@/v2/assets/icons/IceCubesIcon";
import { GridViewIcon } from "@/v2/assets/icons/GridViewIcon";
import { Layers02Icon } from "@/v2/assets/icons/Layers02Icon";
import { TokenSquareIcon } from "@/v2/assets/icons/TokenSquare";
import { GoogleGeminiIcon } from "@/v2/assets/icons/GoogleGeminiIcon";
import { UsersGroupIcon } from "@/v2/assets/icons/UsersGroupIcon";
import { UserIcon } from "@/v2/assets/icons/UserIcon";
import { Comment01Icon } from "@/v2/assets/icons/Comment01Icon";
import { Ticket03Icon } from "@/v2/assets/icons/Ticket03Icon";
import { DropdownMenu } from "@/components/shared/dropdown-menu";
import AiContentGenerator01 from "@/v2/assets/icons/AiContentGenerator01";
import { FlagIcon } from "lucide-react";
import { Internet } from "@/v2/assets/icons/Internet";
import { LayoutListIcon } from "@/v2/assets/icons/LayoutListIcon";
import { AiAgentsIcon } from "@/v2/assets/icons/AiAgents";
import AiProgramming from "@/v2/assets/icons/AiProgramming";
import { StarsIcon } from "@/v2/assets/icons/StarsIcon";

interface ExploreGroupByPickerProps {
  groupBy?: string;
  setGroupBy: (groupBy?: string) => void;
  enrichments?: ExploreEnrichment[];
  allowedGroupByOptions?: ExploreGroupByColumnType[];
  notAllowedGroupByOptions?: ExploreGroupByColumnType[];
}

export function ExploreGroupByPicker({
  groupBy,
  setGroupBy,
  enrichments = [],
  allowedGroupByOptions = [],
  notAllowedGroupByOptions = [],
}: ExploreGroupByPickerProps) {
  const options = useMemo(() => {
    let baseOptions: { label: string; value: string }[];
    if (allowedGroupByOptions.length) {
      baseOptions = allowedGroupByOptions.map((option) => ({
        label: exploreGroupByColumnTypeToLabel[option].plural,
        value: option,
      }));
    } else {
      baseOptions = [
        ...exploreGroupByColumnTypes
          .filter((option) => !notAllowedGroupByOptions.includes(option))
          .map((option) => ({
            label: exploreGroupByColumnTypeToLabel[option].plural,
            value: option,
          }))
          .filter(
            (option) =>
              !notAllowedGroupByOptions.includes(
                option.value as ExploreGroupByColumnType
              )
          ),
        ...enrichments?.map(({ label, value }) => ({
          label,
          value,
        })),
      ];
    }
    return [...baseOptions, { label: "Custom fields", value: "customFields" }];
  }, [enrichments, allowedGroupByOptions, notAllowedGroupByOptions]);

  return (
    <Select
      size="small"
      options={options}
      value={groupBy}
      onChange={(value) => setGroupBy(value === "" ? undefined : value)}
      placeholder="Group By"
      allowClear
    />
  );
}

const groupByToIcon: Record<string, React.ReactNode> = {
  topic: <IceCubesIcon fill="purple" />,
  category: <GridViewIcon fill="indigo" />,
  subTopic: <Layers02Icon fill="plum" />,
  resolutionStatus: <TokenSquareIcon fill="mint" />,
  team: <UsersGroupIcon fill="tomato" />,
  agentAssigned: <UserIcon fill="crimson" />,
  conversations: <Comment01Icon fill="orange" />,
  tickets: <Ticket03Icon fill="blue" />,
  avgQaScoreRoundRange: <GoogleGeminiIcon fill="green" />,
  avgQuackMessageScoreRoundRange: <GoogleGeminiIcon fill="jade" />,
  isQuackAnswered: <AiContentGenerator01 fill="teal" />,
  isAnsweredByAui: <AiContentGenerator01 fill="cyan" />,
  scoreTitle: <FlagIcon fill="green" />,
  originUrl: <Internet fill="purple" />,
  simulator: <AiAgentsIcon fill="purple" />,
  skillUsed: <AiProgramming fill="orange" />,
  avgCsat: <StarsIcon fill="amber" />,
  customFields: <LayoutListIcon fill="slate" />,
};

interface ExploreGroupByPickerTabsProps extends ExploreGroupByPickerProps {
  source: ExploreInteractionType;
  conversationCustomFields?: ExploreCustomField[];
  ticketCustomFields?: ExploreCustomField[];
}

const defaultCustomFieldsIcon = groupByToIcon.customFields;

export function ExploreGroupByPickerTabs({
  source,
  groupBy,
  setGroupBy,
  enrichments = [],
  allowedGroupByOptions = [],
  notAllowedGroupByOptions = [],
  conversationCustomFields = [],
  ticketCustomFields = [],
}: ExploreGroupByPickerTabsProps) {
  const tabs = useMemo(() => {
    const baseTab = {
      label: exploreInteractionTypeToLabel[source].plural,
      value: source,
      icon: groupByToIcon[source],
    };

    let baseTabs: { label: string; value: string; icon: React.ReactNode }[];
    if (allowedGroupByOptions.length) {
      baseTabs = [
        baseTab,
        ...allowedGroupByOptions.map((option) => ({
          label: exploreGroupByColumnTypeToLabel[option].plural,
          value: option,
          icon: groupByToIcon[option],
        })),
      ];
    } else {
      baseTabs = [
        baseTab,
        ...exploreGroupByColumnTypes
          .filter((option) => !notAllowedGroupByOptions.includes(option))
          .map((option) => ({
            label: exploreGroupByColumnTypeToLabel[option].plural,
            value: option,
            icon: groupByToIcon[option],
          }))
          .filter(
            (option) =>
              !notAllowedGroupByOptions.includes(
                option.value as ExploreGroupByColumnType
              )
          ),
      ];
    }

    const customFieldsTabs = (fields: ExploreCustomField[]) => {
      const byValue = new Map<string, ExploreCustomField>();
      fields.forEach((f) => {
        if (!byValue.has(f.value)) {
          byValue.set(f.value, f);
        }
      });
      return Array.from(byValue.values()).map((f) => ({
        label: f.label,
        value: f.value,
        icon: defaultCustomFieldsIcon,
      }));
    };

    if (source === "conversations" && conversationCustomFields.length > 0) {
      return [...baseTabs, ...customFieldsTabs(conversationCustomFields)];
    }

    if (source === "tickets" && ticketCustomFields.length > 0) {
      return [...baseTabs, ...customFieldsTabs(ticketCustomFields)];
    }

    return [...baseTabs];
  }, [
    source,
    allowedGroupByOptions,
    notAllowedGroupByOptions,
    conversationCustomFields,
    ticketCustomFields,
  ]);

  const listRef = useRef<HTMLDivElement>(null);
  const triggerRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const moreButtonRef = useRef<HTMLButtonElement>(null);

  const [visibleCount, setVisibleCount] = useState(tabs.length);

  const measureMoreWidth = useCallback((): number => {
    // If the real button is in the DOM, use it…
    if (moreButtonRef.current) {
      return moreButtonRef.current.offsetWidth;
    }
    // …otherwise, create a hidden one just to measure
    const temp = document.createElement("button");
    temp.className = "px-3 py-1 text-sm font-medium"; // match your TabsTrigger styling
    temp.style.position = "absolute";
    temp.style.visibility = "hidden";
    temp.textContent = "more ...";
    document.body.appendChild(temp);
    const w = temp.offsetWidth;
    document.body.removeChild(temp);
    return w;
  }, []);

  const measureTabWidth = useCallback(
    (tabIndex: number): number => {
      const btn = triggerRefs.current[tabIndex];
      if (btn) {
        return btn.offsetWidth;
      }

      const temp = document.createElement("button");
      temp.className =
        "inline-flex items-center justify-center whitespace-nowrap rounded-md gap-1 font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:text-foreground px-3 py-[6px] text-[14px] data-[state=active]:bg-muted data-[state=active]:shadow-none";
      temp.style.position = "absolute";
      temp.style.visibility = "hidden";
      temp.innerHTML = `${tabs[tabIndex].icon}${tabs[tabIndex].label}`;
      document.body.appendChild(temp);
      const w = temp.offsetWidth;
      document.body.removeChild(temp);
      return w;
    },
    [tabs]
  );

  // Measure which tabs fit
  const updateOverflow = useCallback(() => {
    const listWidth = listRef.current?.offsetWidth ?? 0;
    const moreWidth = measureMoreWidth();

    let used = 0;
    let fit = 0;

    for (let i = 0; i < tabs.length; i++) {
      const w = measureTabWidth(i);
      const willOverflow =
        used + w + (i < tabs.length - 1 ? moreWidth : 0) > listWidth;
      if (willOverflow) {
        break;
      }
      used += w;
      fit++;
    }

    setVisibleCount(fit);
  }, [measureMoreWidth, measureTabWidth]);

  useLayoutEffect(() => {
    updateOverflow();
  }, [tabs]);

  useEffect(() => {
    window.addEventListener("resize", updateOverflow);
    return () => window.removeEventListener("resize", updateOverflow);
  }, [tabs]);

  const visibleTabs = tabs.slice(0, visibleCount);
  const overflowTabs = tabs.slice(visibleCount);

  const value = groupBy ?? source;

  const handleGroupByChange = useCallback(
    (value: string) => {
      if (value === "more") {
        return;
      }

      if (value === source) {
        setGroupBy(undefined);
        return;
      }

      setGroupBy(value);
    },
    [source, setGroupBy]
  );

  return (
    <Tabs className="w-full" value={value} onValueChange={handleGroupByChange}>
      <TabsList
        ref={listRef}
        className="relative flex justify-start overflow-hidden bg-transparent"
      >
        {visibleTabs.map((tab, i) => (
          <TabsTrigger
            key={tab.value}
            value={tab.value}
            ref={(el) => (triggerRefs.current[i] = el)}
            className="data-[state=active]:bg-muted data-[state=active]:shadow-none"
          >
            {tab.icon}
            {tab.label}
          </TabsTrigger>
        ))}

        {overflowTabs.length > 0 && (
          <DropdownMenu
            items={overflowTabs.map((tab) => ({
              label: tab.label,
              value: tab.value,
              icon: tab.icon,
              onClick: () => handleGroupByChange(tab.value),
              active: tab.value === groupBy,
            }))}
          >
            <DropdownMenu.Trigger>
              <TabsTrigger
                ref={moreButtonRef}
                className="flex items-center"
                value={"more"}
              >
                more ...
              </TabsTrigger>
            </DropdownMenu.Trigger>
          </DropdownMenu>
        )}
      </TabsList>
    </Tabs>
  );
}
